import type { Notification } from "./api";

// Dispatcher: each notification type registers a renderer.  Adding a new
// type later is just a new entry in this map — the inbox page picks up
// the new visual treatment without further changes.

type Renderer = (n: Notification) => RenderedNotification;
type RenderedNotification = {
  icon: string;
  title: string;
  body: string | null;
  meta: string | null; // small grey line beneath body (timestamp etc.)
};

const RENDERERS: Record<string, Renderer> = {
  share_link_viewed: (n) => {
    const p = n.payload as {
      title?: string;
      ip?: string | null;
      userAgentSummary?: string | null;
    };
    const where = [p.ip, p.userAgentSummary].filter(Boolean).join(" · ");
    return {
      icon: "👁️",
      title: "Share link viewed",
      body: p.title ?? null,
      meta: where || null,
    };
  },
  contact_request: (n) => {
    const p = n.payload as {
      fromHandle?: string | null;
      fromDisplayName?: string | null;
      fromEmail?: string | null;
      message?: string | null;
    };
    const who = p.fromHandle
      ? `@${p.fromHandle}`
      : p.fromDisplayName || p.fromEmail || "Someone";
    return {
      icon: "👋",
      title: `${who} wants to connect`,
      body: p.message ?? null,
      meta: p.fromEmail ?? null,
    };
  },
  contact_accepted: (n) => {
    const p = n.payload as {
      contactHandle?: string | null;
      contactDisplayName?: string | null;
      contactEmail?: string | null;
    };
    const who = p.contactHandle
      ? `@${p.contactHandle}`
      : p.contactDisplayName || p.contactEmail || "Your contact";
    return {
      icon: "🤝",
      title: `${who} accepted your contact request`,
      body: null,
      meta: p.contactEmail ?? null,
    };
  },
  share_offered: (n) => {
    const p = n.payload as {
      ownerHandle?: string | null;
      ownerDisplayName?: string | null;
      ownerEmail?: string | null;
      title?: string;
      permissions?: string;
      includeSubtree?: boolean;
      scope?: string;
    };
    const who = p.ownerHandle
      ? `@${p.ownerHandle}`
      : p.ownerDisplayName || p.ownerEmail || "Someone";
    const subtree =
      p.scope === "item" && p.includeSubtree ? " (with subtree)" : "";
    return {
      icon: "📂",
      title: `${who} shared "${p.title ?? "an item"}"${subtree}`,
      body: null,
      meta: p.permissions ? `Permission: ${p.permissions}` : null,
    };
  },
  share_accepted: (n) => {
    const p = n.payload as {
      recipientHandle?: string | null;
      recipientDisplayName?: string | null;
      recipientEmail?: string | null;
    };
    const who = p.recipientHandle
      ? `@${p.recipientHandle}`
      : p.recipientDisplayName || p.recipientEmail || "They";
    return {
      icon: "✅",
      title: `${who} accepted your share`,
      body: null,
      meta: p.recipientEmail ?? null,
    };
  },
  share_revoked: (n) => {
    const p = n.payload as { byHandle?: string | null };
    const who = p.byHandle ? `@${p.byHandle}` : "Someone";
    return {
      icon: "🔒",
      title: `Share revoked`,
      body: `${who} revoked or left the share`,
      meta: null,
    };
  },
  share_activity: (n) => {
    const p = n.payload as {
      actorHandle?: string | null;
      actorDisplayName?: string | null;
      actorEmail?: string | null;
      verb?: string;
      itemTitle?: string;
      scope?: "item" | "group";
    };
    const who = p.actorHandle
      ? `@${p.actorHandle}`
      : p.actorDisplayName || p.actorEmail || "Someone";
    const verb = p.verb ?? "edited";
    const title = p.itemTitle ?? "an item";
    return {
      icon:
        verb === "completed"
          ? "✅"
          : verb === "deleted"
            ? "🗑️"
            : verb === "added"
              ? "➕"
              : verb === "added a note to"
                ? "📝"
                : "✏️",
      title: `${who} ${verb} "${title}"`,
      body: null,
      meta: p.scope === "group" ? "in a shared group" : "in a shared item",
    };
  },
  message: (n) => {
    const p = n.payload as {
      fromHandle?: string | null;
      fromDisplayName?: string | null;
      fromEmail?: string | null;
      body?: string;
    };
    const who = p.fromHandle
      ? `@${p.fromHandle}`
      : p.fromDisplayName || p.fromEmail || "Someone";
    return {
      icon: "💬",
      title: `${who} sent a message`,
      body: p.body ?? null,
      meta: null,
    };
  },
  report_addressed: (n) => {
    const p = n.payload as {
      title?: string;
      kind?: "bug" | "feature";
      resolution?: string | null;
      resolverHandle?: string | null;
      resolverDisplayName?: string | null;
      resolverEmail?: string | null;
    };
    const who = p.resolverHandle
      ? `@${p.resolverHandle}`
      : p.resolverDisplayName?.trim() ||
        p.resolverEmail?.split("@")[0] ||
        "An admin";
    return {
      icon: p.kind === "feature" ? "✨" : "✅",
      title: `${who} addressed your ${p.kind === "feature" ? "feature request" : "bug report"}`,
      body: p.title ?? null,
      meta: p.resolution ?? null,
    };
  },
  sync_drift_repaired: (n) => {
    const p = n.payload as { clientGroupId?: string };
    return {
      icon: "🔄",
      title: "Local sync refreshed",
      body: "Your local data was out of sync with the server and has been re-downloaded automatically.",
      meta: p.clientGroupId ? `Device: ${p.clientGroupId.slice(0, 8)}` : null,
    };
  },
  test: (n) => ({
    icon: "🛠",
    title: "Test notification",
    body: JSON.stringify(n.payload),
    meta: null,
  }),
};

export function renderNotification(n: Notification): RenderedNotification {
  const r = RENDERERS[n.type];
  if (r) return r(n);
  // Fallback for unknown types — show what we have so old clients aren't
  // mystified by new server-side types.
  return {
    icon: "🔔",
    title: humanise(n.type),
    body: null,
    meta: null,
  };
}

function humanise(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
